import { faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Controller, useForm } from "react-hook-form";
import { AVAILABLE_USER_PLATFORM_ROLE } from "../../../components/users/list-users/list-users";

const capitalize = (str: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

export type UserCreationFormValues = {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  role: AVAILABLE_USER_PLATFORM_ROLE;
  phone: string;
  note: string;
  servicesCompensations: any[];
};

type Props = {
  confirm?: () => void;
  closeFunction?: () => void;
  customParams: {
    doCreateUser: (user: UserCreationFormValues) => Promise<unknown>;
  };
};

const UserCreateContent = ({
  closeFunction,
  confirm,
  customParams: { doCreateUser },
}: Props) => {
  const { control, handleSubmit, errors, formState, watch } = useForm<{
    data: UserCreationFormValues;
  }>({
    mode: "onBlur",
    defaultValues: {
      data: {
        first_name: "",
        last_name: "",
        email: "",
        username: "",
        role: AVAILABLE_USER_PLATFORM_ROLE.SALES,
        phone: "",
        note: "",
        servicesCompensations: [],
      },
    },
  });

  const d = watch("data");

  const hasInvalid =
    !d?.first_name ||
    !d?.last_name ||
    !d?.email ||
    !d?.username ||
    !d.role ||
    !d?.phone ||
    formState.isSubmitting;

  const onSubmitCreation = handleSubmit(async ({ data }) => {
    const userToCreate: UserCreationFormValues = {
      first_name: capitalize(data.first_name),
      last_name: capitalize(data.last_name),
      email: data.email.trim().toLowerCase(),
      username: data.username.trim(),
      servicesCompensations: data.servicesCompensations ?? [],
      role: data.role,
      phone: data.phone,
      note: data.note,
    };

    await doCreateUser(userToCreate);
    confirm?.();
    closeFunction?.();
  });

  return (
    <div className="modal-body">
      <div className="row g-3">
        <div className="col-6">
          <label className="form-label">Nome</label>
          <Controller
            name={"data.first_name" as any}
            control={control}
            defaultValue=""
            rules={{ required: "Il nome è obbligatorio" }}
            render={({ value, onChange, onBlur }) => (
              <input
                className={`form-control ${
                  errors?.data?.first_name ? "is-invalid" : ""
                }`}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder="Mario"
              />
            )}
          />
          {errors?.data?.first_name && (
            <div className="invalid-feedback">
              {errors.data.first_name.message}
            </div>
          )}
        </div>

        <div className="col-6">
          <label className="form-label">Cognome</label>
          <Controller
            name={"data.last_name" as any}
            control={control}
            defaultValue=""
            rules={{ required: "Il cognome è obbligatorio" }}
            render={({ value, onChange, onBlur }) => (
              <input
                className={`form-control ${
                  errors?.data?.last_name ? "is-invalid" : ""
                }`}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder="Rossi"
              />
            )}
          />
          {errors?.data?.last_name && (
            <div className="invalid-feedback">
              {errors.data.last_name.message}
            </div>
          )}
        </div>

        <div className="col-12">
          <label className="form-label">Email</label>
          <Controller
            name={"data.email" as any}
            control={control}
            defaultValue=""
            rules={{
              required: "Email obbligatoria",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Formato email non valido",
              },
            }}
            render={({ value, onChange, onBlur }) => (
              <input
                type="email"
                className={`form-control ${
                  errors?.data?.email ? "is-invalid" : ""
                }`}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder="mario.rossi@email.com"
              />
            )}
          />
          {errors?.data?.email && (
            <div className="invalid-feedback">{errors.data.email.message}</div>
          )}
        </div>

        <div className="col-6">
          <label className="form-label">Username</label>
          <Controller
            name={"data.username" as any}
            control={control}
            defaultValue=""
            rules={{
              required: "Username obbligatorio",
              minLength: { value: 3, message: "Minimo 3 caratteri" },
            }}
            render={({ value, onChange, onBlur }) => (
              <input
                className={`form-control ${
                  errors?.data?.username ? "is-invalid" : ""
                }`}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder="mrossi"
              />
            )}
          />
          {errors?.data?.username && (
            <div className="invalid-feedback">
              {errors.data.username.message}
            </div>
          )}
        </div>

        <div className="col-6">
          <label className="form-label">Ruolo</label>
          <Controller
            name={"data.role" as any}
            control={control}
            defaultValue={AVAILABLE_USER_PLATFORM_ROLE.SALES}
            rules={{ required: true }}
            render={({ value, onChange }) => (
              <select
                className="form-select"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
              >
                {Object.values(AVAILABLE_USER_PLATFORM_ROLE).map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}
          />
        </div>

        <div className="col-12">
          <label className="form-label">Telefono</label>
          <Controller
            name={"data.phone" as any}
            control={control}
            defaultValue=""
            render={({ value, onChange, onBlur }) => (
              <input
                className="form-control"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder="+39 333 123 4567"
              />
            )}
          />
        </div>

        <div className="col-12">
          <label className="form-label">Note</label>
          <Controller
            name={"data.note" as any}
            control={control}
            defaultValue=""
            render={({ value, onChange, onBlur }) => (
              <textarea
                className="form-control"
                rows={3}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder="Note interne..."
              />
            )}
          />
        </div>

        <div className="col-12 mt-2">
          <button
            className="col-12 mt-3 d-flex gap-2 btn btn-primary align-items-center justify-content-center"
            onClick={onSubmitCreation}
            disabled={hasInvalid}
            type="button"
          >
            <FontAwesomeIcon icon={faSave} />
            Crea utente
          </button>
          <hr />
        </div>
      </div>
    </div>
  );
};

export default UserCreateContent;
